import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/Toast";
import { API_BASE } from "../utils/api";

export default function PaymentCallback() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState("loading"); // loading, success, failure
  const [message, setMessage] = useState("");
  const [reference, setReference] = useState("");

  useEffect(() => {
    const verifyPayment = async () => {
      const ref = searchParams.get("reference");
      if (!ref) {
        setStatus("failure");
        setMessage("No payment reference found. Please try again.");
        return;
      }

      setReference(ref);

      try {
        const res = await fetch(`${API_BASE}/api/payments/verify`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ reference: ref }),
        });

        const data = await res.json();

        if (res.ok) {
          setStatus("success");
          setMessage(data.message || "Payment verified successfully!");
          addToast("Payment successful! Your subscription has been updated.", "success");
        } else {
          setStatus("failure");
          setMessage(data.detail || "Payment verification failed. Please contact support.");
          addToast("Payment verification failed.", "error");
        }
      } catch (err) {
        setStatus("failure");
        setMessage(err.message || "Connection error. Please try again.");
        addToast(err.message || "Connection error", "error");
      }
    };

    verifyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, token]);

  return (
    <div className="min-h-screen bg-luxury-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full rounded-2xl border border-luxury-border bg-luxury-surface p-8 text-center">
        {/* Status Icon */}
        <div className="flex justify-center mb-5">
          {status === "loading" && (
            <div className="w-16 h-16 rounded-full bg-luxury-elevated flex items-center justify-center">
              <svg
                className="animate-spin w-8 h-8 text-luxury-gold"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            </div>
          )}

          {status === "success" && (
            <div className="w-16 h-16 rounded-full bg-luxury-cta/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-luxury-cta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          )}

          {status === "failure" && (
            <div className="w-16 h-16 rounded-full bg-red-900/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-luxury-text mb-2">
          {status === "loading" && "Verifying Payment"}
          {status === "success" && "Payment Successful"}
          {status === "failure" && "Payment Failed"}
        </h1>

        {/* Message */}
        <p className="text-luxury-secondary text-sm mb-6">{message}</p>

        {/* Reference */}
        {reference && (
          <div className="bg-luxury-elevated rounded-xl p-3 mb-6">
            <p className="text-xs text-luxury-muted mb-1">Reference Number</p>
            <p className="text-sm font-mono text-luxury-text break-all">{reference}</p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={() => navigate("/dashboard")}
            className="w-full py-3 rounded-xl font-semibold text-sm transition-all bg-luxury-cta hover:bg-luxury-cta-hover text-white active:scale-95"
          >
            Go to Dashboard
          </button>

          {status === "failure" && (
            <button
              onClick={() => navigate("/subscription")}
              className="w-full py-3 rounded-xl font-semibold text-sm transition-all border border-luxury-border text-luxury-secondary hover:bg-luxury-elevated active:scale-95"
            >
              Try Again
            </button>
          )}

          {status === "success" && (
            <button
              onClick={() => navigate("/subscription")}
              className="w-full py-3 rounded-xl font-semibold text-sm transition-all border border-luxury-border text-luxury-secondary hover:bg-luxury-elevated active:scale-95"
            >
              View Subscription
            </button>
          )}
        </div>

        {/* Help text */}
        <p className="text-xs text-luxury-muted mt-6">
          If you experience any issues, please contact our support team.
        </p>
      </div>
    </div>
  );
}
